/**
 * Store pour les tâches du client connecté dont on gère le retour
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
/***/
import { useAuthCustomerTaskStore } from '@stores/auth/customerTask'

export const useAuthReturnTaskStore = defineStore('authReturnTask', (() => {

    // Store de gestion des tâches du client connecté
    // Utilisé pour synchroniser les statuts des tâches chargées après l'enregistrement d'un retour
    const customerTaskStore = useAuthCustomerTaskStore();

    // State des tâches chargées
    const returnTasks = ref([]);

    /**
     * Retourne la tâche dont l'identifiant public est en paramètre
     * @param int publicId
     * @return object|null
     */
    const getReturnTaskByPublicId = computed(() => {
        return ((publicId) => {
            const index = returnTasks.value.findIndex((taskFromState) => taskFromState.publicId === publicId);
            if(index === -1) {
                return null;
            }
            return returnTasks.value[index];
        });
    });

    /**
     * Chargement des données d'une tâche par identifiant public
     * @param string publicId
     * @param bool force Vrai si on force le rechargement des données
     * @return Promise
     */
    const loadReturnTaskByPublicId = ((publicId, force = false) => {

        const loadedTask = getReturnTaskByPublicId.value(publicId);
        if(loadedTask !== null && ! force) {
            return Promise.resolve();
        }

        const path = '/api/me/tasks/:publicId/return'.replace(':publicId', publicId);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.task) {
                updateReturnTask(response.data.task);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Annulation d'un retour
     * @param string publicId
     * @return Promise
     */
    const cancelReturn = ((publicId) => {

        const path = '/api/me/tasks/:publicId/return'.replace(':publicId', publicId);

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {

                if(response.data.mustForgetReturn) {

                    // Suppression de la référence du retour
                    const indexToDelete = returnTasks.value.findIndex((taskFromState) => taskFromState.publicId === publicId);
                    if(indexToDelete !== -1) {
                        returnTasks.value = returnTasks.value.splice(indexToDelete, 1);
                    }

                    // Mise à jour des données de la tâche
                    if(response.data.task) {
                        customerTaskStore.updateCustomerTask(response.data.task);
                    }
                }

                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Modifie une tâche dans le store
     * @param object task Données de la tâche à mettre à jour dans le store
     * @return Promise
     */
    const updateReturnTask = ((task) => {

        const currentTasks = returnTasks.value;

        const index = currentTasks.findIndex((taskFromState) => taskFromState.publicId === task.publicId);
        if(index !== -1) {
            returnTasks.value[index] = { ...returnTasks.value[index], ...task };
        } else {
            returnTasks.value.push(task);
        }

        // Synchronisation avec le store de la liste des tâches du client connecté
        const customerTask = customerTaskStore.getCustomerTaskByPublicId(task.publicId);
        if(customerTask !== null) {
            const newTaskData = {
                status: task.status,
                publicationsPlanned: task.publicationsPlanned ?? [],
                canRating: task.canRating,
                canDoReturn: task.canDoReturn,
                canCancelPublication: task.canCancelPublication
            };

            if(task.proposals) {
                newTaskData.latestProposals = task.proposals;
            }

            customerTaskStore.updateCustomerTask({
                ...customerTask,
                ...newTaskData
            });
        }

    });

    return {
        getReturnTaskByPublicId,
        loadReturnTaskByPublicId,
        updateReturnTask,
        cancelReturn
    };

}));


